import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { blog6 ,blog7_2} from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower";

const Blog7 = () => {
  const navigate = useNavigate();
  const handleBackClick = () => {
    window.scrollTo(0, 0);
    navigate("/blog");
  };

  const journeyData = [
    ['Prospects', 'Application deadline reminders, event RSVPs, financial aid tips'],
    ['Applicants', 'Missing docs, interview reminders, next steps after submission'],
    ['Admitted Students', 'Orientation info, housing deadlines, FAFSA nudges'],
    ['Enrolled Students', 'Class registration, payment due dates, advising session reminders'],
    ['At-Risk Students', 'Check-ins, encouragement, referral to support services']
  ];

  const journeyHeaders = ['Stage', 'Use Cases'];

  return (
    <>
      <Fullpage>
        <PageBackground>
          <BlogContainer>
            <MainContent>
              <HeaderSection>
                <BackButtonContainer>
                  <BackButton onClick={handleBackClick}>
                    Back to Blogs
                  </BackButton>
                </BackButtonContainer>
                <BlogHeader />
              </HeaderSection>

              <BlogImage
                src={blog6}
                alt="SMS illustration"
              />

              <ArticleContent>
                <ContentSection title="Why Use SMS?">
                  <BodyText>
                    SMS boasts open rates above 90%, with most messages read within minutes.
                    For Gen Z and adult learners alike, it’s a preferred and effective
                    communication channel for time-sensitive, actionable updates.
                  </BodyText>
                </ContentSection>

                <ContentSection title="✏️ 1. Obtain Explicit Consent First">
                <BodyText>
                    Always gather opt-in from students during application, registration, or orientation processes.
                  </BodyText>
                  <BulletList>
                    <li>Use clear language (e.g., “By opting in, you agree to receive important college updates via text.”)</li>
                    <li>Allow students to opt out at any time with a simple keyword like “STOP”</li>
                  </BulletList>
                </ContentSection>

                <ContentSection title="📝 2. Keep Messages Short, Clear, and Actionable">
                 <BodyText>
                    SMS is not email. Focus on urgency, clarity, and one CTA.
                  </BodyText>
                
                  <BulletList>
                    <li>❌ Too long: “We’d like to remind you about the upcoming advising week which begins next Monday. Please check your email for a link to schedule a session.”</li>
                    <li>✅ Better: “Advising starts Monday! Schedule your session here: [shortlink]”</li>
                  </BulletList>
 
                </ContentSection>

                <ContentSection title="⏳ 3. Use SMS Strategically Across the Student Journey">
                  <ComparisonTable data={journeyData} headers={journeyHeaders} />
                   <BodyText>
                    Campuses adopting AI report higher student satisfaction, faster response times, and improved retention rates.
                  </BodyText>
                </ContentSection>

                <ContentSection title="📅 4. Timing Matters">
                  <BlogImage1
                src={blog7_2}
                alt="SMS illustration"
              />
                  <BulletList>
                    <li>Avoid early morning or late-night texts (ideal: 9am–7pm local time)</li>
                    <li>Limit to 1–2 texts/week unless highly time-sensitive</li>
                  </BulletList>
                </ContentSection>

                <ContentSection title="🌐 5. Use Smart Personalization">
                  <BulletList>
                    <li>Student name</li>
                    <li>Program of interest</li>
                    <li>Next deadline</li>
                     <li>✅ Example: “Hi Jordan, your Fall registration is still incomplete. Need help? Reply or visit: [link]”</li>
                  </BulletList>
                </ContentSection>

                <ContentSection title="📊 6. Multilingual & Inclusive Messaging">
                  <BodyText>
                    If students/families prefer another language, configure SMS in Campus Insights’ multilingual engine to reflect that preference. Use culturally aware, inclusive language across all messages.
                  </BodyText>
                </ContentSection>

                <ContentSection title="⚠️ 7. Monitor Performance & Adjust">
                  <BodyText>
                   Track :
                  </BodyText>
                  <BulletList>
                    <li>Delivery & open rates</li>
                    <li>Click-throughs on links</li>
                    <li>Response rates</li>
                    <li>Opt-out trends</li>
                  </BulletList>
                   <BodyText>
                   Use Campus Insights’ analytics to test and optimize message timing, tone, and content.
                  </BodyText>
                </ContentSection>

                <ContentSection title="📜 8. Respect Boundaries & Regulations">
                  <BulletList>
                    <li>Comply with FERPA and TCPA (Telephone Consumer Protection Act)</li>
                    <li>Avoid sensitive personal or academic information in text messages</li>
                    <li>Always offer opt-out: “Reply STOP to opt out”</li>
                  </BulletList>
                </ContentSection>

                <ContentSection title="🎯 Pro Tip: Integrate with Workflows">
                 <BodyText>
                  Use SMS alongside email, chatbot, and web nudges to create multi-channel nudging campaigns within Campus Insights—ensuring consistent and student-friendly communication.
                  </BodyText>
                </ContentSection>

              </ArticleContent>
            </MainContent>
          </BlogContainer>
        <OtherBlogs currentBlogId={7} />
        </PageBackground>
      </Fullpage>
      <CTASection />
    </>
  );
};

// Header component
const BlogHeader = () => {
  return (
    <HeaderContainer>
      <Title>Best Practices for Using SMS in Campus Insights</Title>
      <MetaInfo>
        <DateText>August 6, 2025</DateText>
        <AuthorText>posted by campus insights</AuthorText>
      </MetaInfo>
    </HeaderContainer>
  );
};

// Table component
const ComparisonTable = ({ data, headers }) => {
  return (
    <TableWrapper>
      <Table>
        <HeaderRow>
          {headers.map((header, index) => (
            <HeaderCell key={index}>
              <CellContent>
                <HeaderText>{header}</HeaderText>
              </CellContent>
            </HeaderCell>
          ))}
        </HeaderRow>
        {data.map((row, rowIndex) => (
          <DataRow key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <DataCell key={cellIndex}>
                <CellContent>
                  <CellText>{cell}</CellText>
                </CellContent>
              </DataCell>
            ))}
          </DataRow>
        ))}
      </Table>
    </TableWrapper>
  );
};

// Content section wrapper
const ContentSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <ContentWrapper>{children}</ContentWrapper>
    </SectionContainer>
  );
};

// Image wrapper
const BlogImage = ({ src, alt }) => {
  return (
    <ImageContainer>
      <Image src={src} alt={alt} />
    </ImageContainer>
  );
};

const BlogImage1 = ({ src, alt }) => {
  return (
    <ImageContainer1>
      <Image1 src={src} alt={alt} />
    </ImageContainer1>
  );
};

/* ===== Styled Components from blog6.js ===== */
const ImageContainer = styled.figure`width:100%;margin:0;`;
const Image = styled.img`
  width: 100%;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;
  @media (max-width: 991px){height:300px;}
  @media (max-width: 640px){height:250px;}
`;

const ImageContainer1 = styled.figure`width:60%;margin:0;`;
const Image1 = styled.img`
  width: 100%;
  height: 300px;
  border-radius: 16px;
  object-fit: cover;
  @media (max-width: 991px){height:300px;}
  @media (max-width: 640px){height:250px;}
`;

const SectionContainer = styled.section`
  display:flex;flex-direction:column;align-items:flex-start;gap:16px;width:100%;
`;
const SectionTitle = styled.h2`
  color:#1c1b1f;letter-spacing:-0.32px;width:100%;
  font:600 20px/120% Inter,sans-serif;margin:0;
  @media (max-width:991px){font-size:28px;}
  @media (max-width:640px){font-size:24px;}
`;
const ContentWrapper = styled.div`
  display:flex;flex-direction:column;align-items:flex-start;gap:16px;width:100%;
`;

const TableWrapper = styled.div`display:flex;width:100%;flex-direction:column;align-items:flex-start;`;
const Table = styled.div`
  display:flex;width:100%;flex-direction:column;align-items:flex-start;
  border-radius:4px;border:1px solid #5b5b5b;background-color:#fff;
`;
const HeaderRow = styled.div`display:flex;width:100%;`;
const DataRow = styled.div`display:flex;width:100%;`;
const HeaderCell = styled.div`
  flex:1;border-top:1px solid #5b5b5b;border-left:1px solid #5b5b5b;
  background-color:rgba(255,255,255,0.05);
`;
const DataCell = styled.div`
  flex:1;border-top:1px solid #5b5b5b;border-left:1px solid #5b5b5b;
`;
const CellContent = styled.div`display:flex;padding:10px 12px;width:100%;`;
const HeaderText = styled.span`color:#1c1b1f;font:500 20px/140% Inter,sans-serif;`;
const CellText = styled.span`color:#64636a;font:500 16px/160% Inter,sans-serif;`;

const HeaderContainer = styled.header`display:flex;flex-direction:column;gap:20px;width:100%;`;
const Title = styled.h1`
color:#1c1b1f;
font:700 48px/120% Inter,sans-serif;
margin:0;
  @media (max-width: 991px) {
    font:700 28px/120% Inter,sans-serif;
  }
`;
const MetaInfo = styled.div`display:flex;gap:16px;width:100%;`;
const DateText = styled.time`color:#64636a;letter-spacing:2px;text-transform:uppercase;`;
const AuthorText = styled.span`color:#64636a;letter-spacing:2px;text-transform:uppercase;`;

const BlogContainer = styled.div`
  display:flex;max-width:1120px;padding:56px;flex-direction:column;gap:64px;
  border-radius:24px;margin:0 auto;background-color:#fff;
`;
const MainContent = styled.main`display:flex;flex-direction:column;gap:42px;width:100%;`;
const HeaderSection = styled.div`display:flex;flex-direction:column;gap:32px;width:100%;`;
const ArticleContent = styled.article`display:flex;flex-direction:column;gap:20px;width:100%;`;

const BodyText = styled.p`color:#64636a;font:500 16px/160% Inter,sans-serif;margin:0;`;
const BulletList = styled.ul`
  color:#64636a;font:500 16px/160% Inter,sans-serif;margin:0;padding-left:20px;
  li{margin-bottom:8px;}
`;

const PageBackground = styled.div`
  width:100%;
  background:linear-gradient(to bottom,#5e2bff 0px,#5e2bff 500px,#F5F5F9 100px,#F5F5F9 100%);
  display:flex;flex-direction:column;align-items:center;padding:200px 0;
`;
const Fullpage = styled.div`width:100%;display:flex;justify-content:center;`;
const BackButtonContainer = styled.div`display:flex;width:100%;gap:16px;`;

export default Blog7;
